import { useState, useEffect, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useAgentWorkflowStore } from "@/store/agentsStore";
import { useOrganizationStore } from "@/store/organizationStore";
import { AgentsTabController } from "@/components/AgentsTabController";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { toast } from "sonner";

/**
 * AI Workers settings panel for managing agent workflows and primary agent selection.
 * Uses the standard settings UX pattern with section labels, settings rows, and skeleton loading.
 * @returns AI Workers settings panel element
 */
export function AIWorkersSettingsPanel() {
  const organization = useOrganizationStore((state) => state.organization);
  const updateOrganization = useOrganizationStore(
    (state) => state.updateOrganization,
  );

  const agentMap = useAgentWorkflowStore((state) => state.agentMap);
  const activeAgents = useMemo(
    () =>
      Array.from(agentMap.values())
        .filter((agent) => agent.status === "ACTIVE")
        .sort((left, right) => left.name.localeCompare(right.name)),
    [agentMap],
  );

  const [primaryAgentWorkflowId, setPrimaryAgentWorkflowId] = useState<
    string | null
  >(organization?.primaryAgentWorkflowId ?? null);
  const [isUpdatingPrimaryAgent, setIsUpdatingPrimaryAgent] = useState(false);

  useEffect(() => {
    setPrimaryAgentWorkflowId(organization?.primaryAgentWorkflowId ?? null);
  }, [organization?.primaryAgentWorkflowId]);

  const selectedPrimaryAgentId = useMemo(() => {
    if (!primaryAgentWorkflowId) {
      return undefined;
    }
    const hasAgent = activeAgents.some(
      (agent) => agent.id === primaryAgentWorkflowId,
    );
    return hasAgent ? primaryAgentWorkflowId : undefined;
  }, [activeAgents, primaryAgentWorkflowId]);

  /**
   * Handles changing the primary agent by optimistically updating local state
   * and rolling back on failure.
   * @param value - The agent workflow ID to set as primary
   */
  const handlePrimaryAgentChange = useCallback(
    async (value: string) => {
      if (!organization) {
        return;
      }

      if (value === primaryAgentWorkflowId) {
        return;
      }

      const previousValue = primaryAgentWorkflowId;
      setPrimaryAgentWorkflowId(value);
      setIsUpdatingPrimaryAgent(true);
      const success = await updateOrganization({
        primaryAgentWorkflowId: value,
      });
      setIsUpdatingPrimaryAgent(false);

      if (!success) {
        setPrimaryAgentWorkflowId(previousValue);
        toast.error("Failed to update primary agent.");
        return;
      }

      toast.success("Primary agent updated.");
    },
    [organization, primaryAgentWorkflowId, updateOrganization],
  );

  return (
    <div className="space-y-6 pt-4">
      {/* ── Configuration ── */}
      <SectionLabel title="Configuration" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SettingsRow
            id="ai-workers-primary-agent"
            label="Primary Agent"
            description="Shows in the sidebar and opens by default."
          >
            <div className="flex items-center gap-2">
              <Select
                value={selectedPrimaryAgentId}
                onValueChange={handlePrimaryAgentChange}
                disabled={isUpdatingPrimaryAgent || activeAgents.length === 0}
              >
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Select a primary agent" />
                </SelectTrigger>
                <SelectContent>
                  {activeAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isUpdatingPrimaryAgent && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              )}
            </div>
          </SettingsRow>
        </div>
      </Card>

      {/* ── Agents List ── */}
      <Separator />
      <SectionLabel title="Agents" />
      <AgentsTabController />
    </div>
  );
}

// ── Local Components ──

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}
